import {
  Box,
  Button as MUIButton,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { ReactElement, useEffect, useState } from 'react';
import {
  BulkDeleteButton,
  CreateButton,
  Datagrid,
  FunctionField,
  List,
  Filter,
  TextField as ReactAdminTextField,
  useTranslate,
  DeleteButton,
  EditButton,
  useNotify,
  useRedirect,
  useRefresh,
  TextInput,
  DateInput,
  SelectInput,
} from 'react-admin';
import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import CreateTask from './mutations/Create';
import CreateAccumulatedMEMETradingVolumeTask from './mutations/CreateAccumulatedMEMETradingVolumeTask';
import CreateConsecutiveCheckinTask from './mutations/CreateConsecutiveCheckinTask';
import EditTask from './mutations/Edit';
import JsonDetailViewer from '../../../components/JsonDetailViewer';
// 导入数据提供者初始化
import './init';
import FilterGroup from 'src/pages/admin/components/FilterGroup';
import { getCategoryChoices } from '../categories/service';
import { baseUrl, httpClient } from '../../../providers/AppDataProvider';

const ListFilter = (
  props: any & { categoryChoices?: Array<{ id: number | string; name: string }>; translate: any }
) => (
  <Filter {...props}>
    <FilterGroup alwaysOn spacing={0} p={1} pt={2}>
      <TextInput source="id" label={props.translate('activity.tasks.taskId')} alwaysOn />
    </FilterGroup>
    <FilterGroup alwaysOn spacing={0} p={1} pt={2}>
      <DateInput source="startDate" label={props.translate('activity.common.fromTime')} />
    </FilterGroup>
    <FilterGroup alwaysOn spacing={0} p={1} pt={2}>
      <DateInput source="endDate" label={props.translate('activity.common.toTime')} />
    </FilterGroup>
    <FilterGroup alwaysOn spacing={0} p={1} pt={2}>
      <SelectInput
        source="categoryId"
        label={props.translate('activity.common.category')}
        choices={props.categoryChoices || []}
        alwaysOn
      />
    </FilterGroup>
  </Filter>
);

const View = () => {
  const translate = useTranslate();
  const redirect = useRedirect();
  const [popup, setPopup] = useState<ReactElement>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [confirmOpen, setConfirmOpen] = useState<false | 'daily' | 'weekly' | 'monthly' | 'seed'>(
    false
  );
  const [categoryChoices, setCategoryChoices] = useState<
    Array<{ id: number | string; name: string }>
  >([]);
  const [quickTaskId, setQuickTaskId] = useState<string>('');

  useEffect(() => {
    getCategoryChoices()
      .then(setCategoryChoices)
      .catch(() => setCategoryChoices([]));
  }, []);

  const handleConfirm = async () => {
    if (!confirmOpen) return;
    try {
      const map: any = {
        daily: 'adminResetDailyTasks',
        weekly: 'adminResetWeeklyTasks',
        monthly: 'adminResetMonthlyTasks',
        seed: 'adminSeedInitialTasks',
      };
      const url = `${baseUrl}/activity/${map[confirmOpen]}`;
      await httpClient(url, { method: 'POST' });
      notify(translate('activity.common.success'), { type: 'success' });
      refresh();
    } catch (e) {
      notify(translate('activity.common.failed'), { type: 'error' });
    } finally {
      setConfirmOpen(false);
    }
  };
  return (
    <PermissionGuard resource="activity-task" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('resource.activityTasks')}
          links={[
            { name: translate('resource.activity') },
            { name: translate('resource.activityTasks') },
          ]}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                label={translate('activity.tasks.openTaskById')}
                size="small"
                variant="outlined"
                value={quickTaskId}
                onChange={(e) => setQuickTaskId(e.target.value)}
                sx={{ minWidth: 200 }}
              />
              <MUIButton
                variant="contained"
                size="small"
                onClick={() => {
                  const val = quickTaskId.trim();
                  if (!val) return;
                  window.open(
                    `${baseUrl}/activity/tasksByIds?taskIds=[${encodeURIComponent(val)}]`,
                    '_blank'
                  );
                }}
              >
                {translate('activity.common.open')}
              </MUIButton>
              <PermissionGuard resource="activity-task" action="create" fallback={<></>}>
                <CreateButton
                  label={translate('activity.tasks.createOrdinaryTask')}
                  variant="contained"
                  size="medium"
                  onClick={(e) => {
                    e.preventDefault();
                    setPopup(<CreateTask onClose={() => setPopup(undefined)} />);
                  }}
                />
                <CreateButton
                  label={translate('activity.tasks.createConsecutiveTask')}
                  variant="contained"
                  size="medium"
                  onClick={(e) => {
                    e.preventDefault();
                    setPopup(<CreateConsecutiveCheckinTask onClose={() => setPopup(undefined)} />);
                  }}
                />
                <CreateButton
                  label={translate('activity.tasks.createMEMETradingTask')}
                  variant="contained"
                  size="medium"
                  onClick={(e) => {
                    e.preventDefault();
                    setPopup(
                      <CreateAccumulatedMEMETradingVolumeTask onClose={() => setPopup(undefined)} />
                    );
                  }}
                />
              </PermissionGuard>
              <PermissionGuard resource="activity-task" action="update" fallback={<></>}>
                <MUIButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => setConfirmOpen('daily')}
                >
                  {translate('activity.tasks.resetDaily')}
                </MUIButton>
                <MUIButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => setConfirmOpen('weekly')}
                >
                  {translate('activity.tasks.resetWeekly')}
                </MUIButton>
                <MUIButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => setConfirmOpen('monthly')}
                >
                  {translate('activity.tasks.resetMonthly')}
                </MUIButton>
                <MUIButton
                  variant="outlined"
                  color="primary"
                  onClick={() => setConfirmOpen('seed')}
                >
                  {translate('activity.tasks.seedInitial')}
                </MUIButton>
              </PermissionGuard>
            </Stack>
          }
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            resource="activity-task"
            actions={false}
            empty={false}
            exporter={false}
            filters={<ListFilter categoryChoices={categoryChoices} translate={translate} />}
          >
            <Datagrid
              rowClick={false}
              bulkActionButtons={
                <PermissionGuard resource="activity-task" action="delete" fallback={<></>}>
                  <BulkDeleteButton
                    mutationMode="pessimistic"
                    mutationOptions={{
                      onSuccess: () => notify(translate('activity.tasks.updatedSuccessfully'), { type: 'success' }),
                    }}
                  />
                </PermissionGuard>
              }
            >
              <ReactAdminTextField source="id" label={translate('activity.common.id')} sortable={false} />
              <FunctionField
                label={translate('activity.common.category')}
                render={(r: any) => {
                  const c = categoryChoices.find((x) => `${x.id}` === `${r?.categoryId}`);
                  return c?.name ?? r?.categoryId ?? '';
                }}
                sortable={false}
              />
              <ReactAdminTextField
                source="taskIdentifier"
                label={translate('activity.tasks.taskIdentifier')}
                sortable={false}
              />
              <FunctionField
                label={translate('activity.tasks.nameEn')}
                render={(r: any) => r?.name?.en || ''}
                sortable={false}
              />
              <ReactAdminTextField source="points" label={translate('activity.common.points')} sortable={false} />
              <FunctionField
                label={translate('activity.common.active')}
                render={(r: any) => (r?.isActive ? 'Yes' : 'No')}
                sortable={false}
              />
              <FunctionField
                label={translate('activity.common.detail')}
                render={(record: any) => (
                  <JsonDetailViewer data={record} maxLength={20} label={translate('activity.common.detail')} />
                )}
                sortable={false}
              />
              <FunctionField
                label={translate('activity.common.actions')}
                render={(record: any) => (
                  <Stack direction="row" spacing={2}>
                    <PermissionGuard resource="activity-task" action="update" fallback={<></>}>
                      <EditButton
                        onClick={(e) => {
                          e.preventDefault();
                          setPopup(
                            <EditTask
                              id={record.id}
                              resource={record}
                              onClose={() => setPopup(undefined)}
                            />
                          );
                        }}
                      />
                    </PermissionGuard>
                    <PermissionGuard resource="activity-task" action="delete" fallback={<></>}>
                      <DeleteButton
                        mutationMode="pessimistic"
                        mutationOptions={{
                          onSuccess: () => redirect('/activity/tasks'),
                        }}
                      />
                    </PermissionGuard>
                  </Stack>
                )}
              />
            </Datagrid>
          </List>
        </Card>

        <Dialog open={Boolean(popup)} onClose={() => setPopup(undefined)} maxWidth="md" fullWidth>
          {popup}
        </Dialog>

        {Boolean(confirmOpen) && (
          <Dialog
            open={Boolean(confirmOpen)}
            onClose={() => setConfirmOpen(false)}
            maxWidth="xs"
            fullWidth
            keepMounted
            disableEnforceFocus
            disableAutoFocus
            disableRestoreFocus
          >
            <DialogTitle>{translate('activity.tasks.confirmReset')}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {confirmOpen === 'daily' && translate('activity.tasks.resetDailyConfirm')}
                {confirmOpen === 'weekly' && translate('activity.tasks.resetWeeklyConfirm')}
                {confirmOpen === 'monthly' && translate('activity.tasks.resetMonthlyConfirm')}
                {confirmOpen === 'seed' && translate('activity.tasks.seedInitialConfirm')}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <MUIButton onClick={() => setConfirmOpen(false)}>{translate('activity.common.cancel')}</MUIButton>
              <MUIButton color="primary" variant="contained" onClick={handleConfirm} autoFocus>
                {translate('activity.common.confirm')}
              </MUIButton>
            </DialogActions>
          </Dialog>
        )}
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
